import React, { useState } from "react";
import { useYouTubeFeed } from "../hooks/useYouTubeFeed";
import { YouTubeVideo } from "../types/youtubeFeed";
import { YouTubeIcon } from "./MediaKitSocialIcons";
import { formatDuration, formatVideoMeta } from "../utils/youtubeFormat";
import VideoLightbox from "./VideoLightbox";

const CHANNEL_URL = "https://www.youtube.com/@polarisathlete";

/**
 * Homepage YouTube feed section.
 *
 * Renders a white card with up to 3 latest video thumbnails from the
 * Polaris Athlete channel. Clicking a thumbnail opens VideoLightbox;
 * "Watch on YouTube" links open the video on youtube.com in a new tab.
 * When the API returns no videos (or is unavailable) nothing is rendered.
 */
export function YouTubeFeed() {
    const { videos, loading, error } = useYouTubeFeed();
    const [selectedVideo, setSelectedVideo] = useState<YouTubeVideo | null>(null);

    if (loading || error || videos.length === 0) {
        return null;
    }

    return (
        <>
            <section className="landing-youtube" aria-label="Polaris Athlete on YouTube">
                <div className="landing-youtube-card">
                    <div className="landing-youtube-header">
                        <div className="landing-youtube-header-text">
                            <p className="landing-youtube-label">
                                <span className="landing-youtube-label-diamond" aria-hidden="true">✦</span> Polaris Athlete Videos
                            </p>
                            <h2 className="landing-youtube-heading">
                                Watch @polarisathlete On YouTube
                            </h2>
                        </div>
                        <a
                            className="landing-youtube-channel-link"
                            href={CHANNEL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit Polaris Athlete on YouTube"
                        >
                            <span className="landing-youtube-visit-text">Visit</span>
                            <YouTubeIcon className="landing-youtube-logo" />
                            <span className="landing-youtube-logo-text">YouTube</span>
                        </a>
                    </div>

                    <div className="landing-youtube-grid">
                        {videos.map((video) => {
                            const duration = formatDuration(video.duration);
                            const meta = formatVideoMeta(video.viewCount, video.publishedAt);
                            return (
                                <article key={video.videoId} className="landing-youtube-item">
                                    <button
                                        type="button"
                                        className="landing-youtube-thumb-btn"
                                        onClick={() => setSelectedVideo(video)}
                                        aria-label={`Play ${video.title}`}
                                    >
                                        <span className="landing-youtube-thumb-wrap">
                                            <img
                                                src={video.thumbnailUrl}
                                                alt=""
                                                className="landing-youtube-thumb"
                                                loading="lazy"
                                            />
                                            <span className="landing-youtube-play" aria-hidden="true" />
                                            {duration && (
                                                <span className="landing-youtube-duration">{duration}</span>
                                            )}
                                        </span>
                                    </button>
                                    <h3 className="landing-youtube-title">{video.title}</h3>
                                    {meta && <p className="landing-youtube-meta">{meta}</p>}
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <VideoLightbox
                open={selectedVideo !== null}
                resource={
                    selectedVideo
                        ? {
                              id: 0,
                              title: selectedVideo.title,
                              vimeoUrl: selectedVideo.videoUrl,
                          }
                        : null
                }
                onClose={() => setSelectedVideo(null)}
            />
        </>
    );
}

export default YouTubeFeed;
